/* eslint-disable complexity */
import { Badge, Button, buttonVariants } from '@e2n/ui';
import { ArbeitsbereichStation } from '@/domain/mitarbeiter/features/rotationsplan/entities/Station';
import { Abschnitt as AbschnittType } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abschnitt';
import {
  faCalendarRange,
  faChevronDown,
  faChevronUp,
  // faFileLines,
  faPenToSquare,
  faPlusLarge,
  faUser,
  faStar as faStarEmpty,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { faCircle, faStar } from '@fortawesome/pro-solid-svg-icons';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Link, useParams } from 'react-router-dom';
import { StationContainer } from '@/domain/mitarbeiter/features/rotationsplan/features/Details/StationContainer';
import { Location } from '@/domain/mitarbeiter/features/rotationsplan/components/Location';
import { DeleteAbschnitt } from '@/domain/mitarbeiter/features/rotationsplan/features/DeleteAbschnitt/DeleteAbschnitt.tsx';
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan.ts';
import { AbwesenheitenPopover } from '@/domain/mitarbeiter/features/rotationsplan/features/Details/AbwesenheitenPopover';
import { KonfliktIndicator } from '@/domain/mitarbeiter/features/rotationsplan/features/Details/KonfliktIndicator';
import { ErinnerungenInfo } from '@/domain/mitarbeiter/features/rotationsplan/components/ErinnerungenInfo';

/* eslint-disable max-lines-per-function */
export function Abschnitt({
  abschnitt,
  rotationsplan,
}: {
  abschnitt: AbschnittType;
  rotationsplan: Rotationsplan;
}) {
  const { id } = useParams();
  const [isOpen, setIsOpen] = useState(true);
  const { t } = useTranslation();

  return (
    <div key={abschnitt.id} className="flex flex-col gap-5">
      <div className="flex items-center gap-2 px-1 py-1 mt-8 md:hover:bg-neutrals-300 hover:rounded-md group">
        <Button
          variant="ghost"
          className="md:hover:bg-neutrals-400"
          size="icon"
          onClick={() => setIsOpen(!isOpen)}>
          <FontAwesomeIcon
            className="text-neutrals-700"
            icon={isOpen ? faChevronUp : faChevronDown}
          />
        </Button>
        <div className="font-bold">
          {abschnitt.position + '. ' + abschnitt.name}
        </div>
        <div className="block ml-auto md:hidden md:group-hover:block md:ml-0">
          <DeleteAbschnitt
            abschnitt={abschnitt}
            rotationsplan={rotationsplan}
          />
        </div>
      </div>
      <div className="flex flex-col">
        {abschnitt.stationen
          .toSorted((stationA, stationB) => {
            if (stationA.von < stationB.von) return -1;
            if (stationA.von > stationB.von) return 1;
            return 0;
          })
          .map((station) => {
            const abwesenheiten = rotationsplan.abwesenheitenByStationId(
              station.id,
            );
            const konfliktStationen =
              station instanceof ArbeitsbereichStation
                ? rotationsplan.getUeberschneidendeStationen(station)
                : [];

            const borderClass =
              konfliktStationen.length > 0
                ? 'border-primary-red-400 border-solid border-2'
                : station.istAktiv
                  ? 'border-primary-blue-600 border-solid border-2 shadow-md'
                  : '';

            return station instanceof ArbeitsbereichStation ? (
              isOpen && (
                <StationContainer station={station} key={station.id}>
                  <div
                    className={`mb-4 w-full bg-white rounded-md flex justify-between items-start p-4 ${borderClass}`}>
                    <div className="flex flex-col gap-3">
                      <div className="flex flex-col gap-1">
                        <div className="flex flex-col items-start gap-2 md:flex-row md:items-center">
                          <div className="flex items-center gap-2">
                            <div className="text-sm">
                              <FontAwesomeIcon
                                icon={faCircle}
                                color={station.arbeitsbereich.getBorderColor()}
                                size="xs"
                              />
                            </div>
                            <div className="font-bold">
                              {station.arbeitsbereich.name}
                            </div>
                          </div>
                          {abwesenheiten.length > 0 && (
                            <div className="flex items-center gap-0.5">
                              <Badge type="pill" variant="blue">
                                {abwesenheiten.length > 1
                                  ? t(
                                      'domain.mitarbeiter.rotationsplan.rotationsplaene.abwesenheiten_other',
                                      { count: abwesenheiten.length },
                                    )
                                  : t(
                                      'domain.mitarbeiter.rotationsplan.rotationsplaene.abwesenheiten_one',
                                      { count: abwesenheiten.length },
                                    )}
                              </Badge>
                              <AbwesenheitenPopover
                                abwesenheiten={abwesenheiten}
                              />
                            </div>
                          )}
                        </div>
                        {station.lernziele && station.lernziele.length > 0 && (
                          <div className="text-sm text-neutrals-700 line-clamp-3">
                            {station.lernziele}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col items-start gap-2 text-sm md:flex-row md:gap-4 md:items-center text-neutrals-700">
                        <div className="flex items-center gap-1">
                          <FontAwesomeIcon icon={faCalendarRange} />
                          {dayjs(station.von).format('DD.MM.YYYY') +
                            ' – ' +
                            dayjs(station.bis).format('DD.MM.YYYY')}
                        </div>
                        {station.abteilungsleiter && (
                          <div className="flex items-center gap-1">
                            <FontAwesomeIcon icon={faUser} />
                            {station.abteilungsleiter.getName()}
                          </div>
                        )}
                        <Location
                          className="text-sm"
                          standort={station.arbeitsbereich.standort}
                        />
                        {station.frist &&
                          station.frist.erinnerungenAm.length && (
                            <ErinnerungenInfo
                              className="text-sm"
                              frist={station.von}
                              erinnerungenAm={station.frist.erinnerungenAm}
                            />
                          )}
                        <KonfliktIndicator
                          konfliktStationen={konfliktStationen}
                        />
                      </div>
                    </div>
                    <div className="flex flex-row gap-2">
                      {station.bewertung &&
                        (station.bewertung.sterne !== 0 ? (
                          <Link
                            className={cn(
                              buttonVariants({
                                variant: 'ghost-primary',
                              }),
                              'flex flex-row gap-1 items-center -mt-2 text-sm',
                            )}
                            to={`/mitarbeiter/rotationsplan/${id}/${abschnitt.id}/station/${station.id}/bewertung/${station.bewertung.id}`}>
                            <FontAwesomeIcon
                              className="text-neutrals-800"
                              icon={faStar}
                              size="xs"
                            />
                            <div className="text-neutrals-900">
                              {station.bewertung.sterne} / 5
                            </div>
                          </Link>
                        ) : (
                          <Link
                            className={cn(
                              buttonVariants({
                                variant: 'ghost-primary',
                              }),
                              'flex flex-row gap-1 items-center -mt-2 text-sm',
                            )}
                            to={`/mitarbeiter/rotationsplan/${id}/${abschnitt.id}/station/${station.id}/bewertung/${station.bewertung.id}`}>
                            <FontAwesomeIcon
                              className="text-neutrals-800"
                              icon={faStarEmpty}
                              size="xs"
                            />
                            <div className="text-neutrals-900">--</div>
                          </Link>
                        ))}
                      {/* will be added for attachment
                      station.bewertung && (
                        <Link
                          className={cn(
                            buttonVariants({
                              variant: 'ghost-primary',
                              size: 'icon',
                            }),
                            '-mt-2',
                            'text-primary-blue-600',
                          )}>
                          <FontAwesomeIcon size="lg" icon={faFileLines} />
                        </Link>
                      )
                        */}
                      {!station.bewertung &&
                        (station.istAktiv || station.istAbgeschlossen) && (
                          <Link
                            className={cn(
                              buttonVariants({
                                variant: 'outline',
                              }),
                              '-mt-2',
                            )}
                            to={`/mitarbeiter/rotationsplan/${id}/${abschnitt.id}/station/${station.id}/bewertung/create`}>
                            {t(
                              'domain.mitarbeiter.rotationsplan.rotationsplaene.bewerten',
                            )}
                          </Link>
                        )}
                      <Link
                        className={cn(
                          buttonVariants({
                            variant: 'ghost-primary',
                            size: 'icon',
                          }),
                          '-mt-2',
                        )}
                        to={`/mitarbeiter/rotationsplan/${id}/${abschnitt.id}/station/${station.id}/update`}>
                        <FontAwesomeIcon icon={faPenToSquare} size="lg" />
                      </Link>
                    </div>
                  </div>
                </StationContainer>
              )
            ) : (
              <div key={station.id}>Wird noch nicht angezeigt</div>
            );
          })}
        {isOpen && (
          <StationContainer showGradient>
            <div className="flex justify-start w-full p-4 bg-white rounded-md">
              <Link
                to={`/mitarbeiter/rotationsplan/${id}/${abschnitt.id}/station/create`}
                className={cn(
                  buttonVariants({ variant: 'ghost-primary' }),
                  'flex items-center gap-2',
                )}>
                <FontAwesomeIcon icon={faPlusLarge} />
                {t(
                  'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.label',
                )}
              </Link>
            </div>
          </StationContainer>
        )}
      </div>
    </div>
  );
}
